import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';

const NOPTemplate = ({children}) => <>{children}</>;
NOPTemplate.propTypes = {
    children: PropTypes.node
}

const NOPLoadMarker = forwardRef((props, ref) => {
    return <div ref={ref}>&nbsp;</div>
});

const DEFAULT_TOOLS = { slice: (data, ...args) => data.slice(...args), length: data => data.length };

const InvertedInfiniteScroll = ({ data, renderItem, batchSize = 40, style, tools = DEFAULT_TOOLS, Template = NOPTemplate, LoadMarker = NOPLoadMarker, ...props }) => {
    const [visibleItems, setVisibleItems] = useState([]);
    const observer = useRef(null);
    const loadingRef = useRef(null);
    const containerRef = useRef(null);
    const loadInProgress = useRef(false);

    useEffect(() => {
        if (!loadInProgress.current) {
            loadInProgress.current = 0;
            setVisibleItems(() => {
                if (batchSize >= tools.length(data)) {
                    return data;
                } else if (batchSize === Infinity) {
                    return data;
                } else {
                    return tools.slice(data, -batchSize);
                }
            });
            setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.scrollTop = containerRef.current.scrollHeight;
                    loadInProgress.current = false;
                }
            }, 20)
        }
    }, [data, batchSize, tools])

    // Intersection Observer callback
    const handleObserver = useCallback(entries => {
        const target = entries[0];
        if (target.isIntersecting) {
            if (loadInProgress.current === false) {
                loadInProgress.current = containerRef.current?.scrollHeight;
                setVisibleItems(items => {
                    if (tools.length(items) + batchSize >= tools.length(data)) {
                        return data;
                    } else if (batchSize === Infinity) {
                        return data;
                    } else {
                        return tools.slice(data, -tools.length(items) - batchSize);
                    }
                });
                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = containerRef.current.scrollHeight - loadInProgress.current;
                        loadInProgress.current = false;
                    }
                }, 20)
            }
        }
    }, [data, batchSize, tools]);

    // Setup and cleanup the observer
    useEffect(() => {
        if (loadingRef.current) {
            observer.current = new IntersectionObserver(handleObserver, {
                root: containerRef.current, // assuming viewport as root
                rootMargin: '10px',
                threshold: 0.1
            });
            observer.current.observe(loadingRef.current);
        }

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, [loadingRef, handleObserver]);

    const dataLength = tools.length(data);
    const visibleItemsLength = tools.length(visibleItems);

    return (
        <div ref={containerRef} style={{ overflowY: 'auto', height: '100%', ...style }} {...props}>
            <Template>
                {visibleItemsLength < dataLength && <NOPLoadMarker ref={loadingRef}></NOPLoadMarker>}
                {visibleItems.map((note, idx) => renderItem(note, dataLength - visibleItemsLength + idx))}
            </Template>
        </div>
    );
}

InvertedInfiniteScroll.propTypes = {
    data: PropTypes.array,
    renderItem: PropTypes.func,
    batchSize: PropTypes.number,
    style: PropTypes.object,
    Template: PropTypes.func,
    tools: PropTypes.shape({ length: PropTypes.func.isRequired, slice: PropTypes.func.isRequired })
}

export default InvertedInfiniteScroll;
